import { sideBarMenuAllOptions, sideBarMenuFilters } from "@/src/constants/sidebar";
import { ISideBarList } from "@/src/types/sideBar";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getUserConfigFromStorage, getSidebarOpenFromStorage, saveSidebarOpenToStorage } from "@/src/utils/userConfigStorage";

interface ISideBarMenuSlice {
  sideBarMenuAllOptionsState: ISideBarList;
  selectedIds: string[];
  customization: boolean;
  isSidebarOpen: boolean;
}

const getAllIds = (): string[] =>
  Object.values(sideBarMenuFilters)
    .flat()
    .map((item) => item.id);

const getSelectedIdsFromConfig = (config: ISideBarList): string[] => {
  return Object.values(config)
    .flat()
    .map((item) => item.id);
};

const userConfig = getUserConfigFromStorage();
const sidebarList = userConfig?.SideBarConfig?.list || sideBarMenuAllOptions;

const initialState: ISideBarMenuSlice = {
  sideBarMenuAllOptionsState: sidebarList,
  selectedIds: getSelectedIdsFromConfig(sidebarList),
  customization: false,
  isSidebarOpen: getSidebarOpenFromStorage(),
};

const sideBarMenuSlice = createSlice({
  name: "sideBarMenuSlice",
  initialState,
  reducers: {
    toggleItem(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((itemId) => itemId !== id);
      } else {
        state.selectedIds.push(id);
      }
      if (state.customization) {
        state.sideBarMenuAllOptionsState = {
          main: sideBarMenuAllOptions.main.filter((item) => state.selectedIds.includes(item.id)),
          tools: sideBarMenuAllOptions.tools.filter((item) => state.selectedIds.includes(item.id)),
          adminPanel: sideBarMenuAllOptions.adminPanel.filter((item) => state.selectedIds.includes(item.id)),
        };
      }
    },
    toggleSidebar(state) {
      state.isSidebarOpen = !state.isSidebarOpen;
      saveSidebarOpenToStorage(state.isSidebarOpen);
    },
    toggleOpenCustomization(state) {
      state.customization = true;
    },
    toggleCloseCustomization(state) {
      state.customization = false;
    },
    chooseAll(state) {
      state.selectedIds = getAllIds();
      if (state.customization) {
        state.sideBarMenuAllOptionsState = {
          main: sideBarMenuAllOptions.main.filter((item) => state.selectedIds.includes(item.id)),
          tools: sideBarMenuAllOptions.tools.filter((item) => state.selectedIds.includes(item.id)),
          adminPanel: sideBarMenuAllOptions.adminPanel.filter((item) => state.selectedIds.includes(item.id)),
        };
      }
    },
    clearAll(state) {
      state.selectedIds = [];
      if (state.customization) {
        state.sideBarMenuAllOptionsState = {
          main: sideBarMenuAllOptions.main.filter((item) => state.selectedIds.includes(item.id)),
          tools: sideBarMenuAllOptions.tools.filter((item) => state.selectedIds.includes(item.id)),
          adminPanel: sideBarMenuAllOptions.adminPanel.filter((item) => state.selectedIds.includes(item.id)),
        };
      }
    },
    setSidebarConfig(
      state,
      action: PayloadAction<{
        list?: ISideBarList;
        isOpen?: boolean;
      }>
    ) {
      if (action.payload.list) {
        state.sideBarMenuAllOptionsState = action.payload.list;
        state.selectedIds = getSelectedIdsFromConfig(action.payload.list);
      }
      // isOpen не устанавливается из конфигурации с бека - оно управляется только через localStorage
      // При необходимости можно раскомментировать:
      // if (action.payload.isOpen !== undefined) {
      //   state.isSidebarOpen = action.payload.isOpen;
      //   localStorage.setItem("isSidebarOpen", String(action.payload.isOpen));
      // }
    },
    reorderSidebarItems(
      state,
      action: PayloadAction<{
        section: keyof ISideBarList;
        dragIndex: number;
        hoverIndex: number;
      }>
    ) {
      const { section, dragIndex, hoverIndex } = action.payload;
      const sectionItems = [...state.sideBarMenuAllOptionsState[section]];

      // Если индексы одинаковые, ничего не делаем
      if (dragIndex === hoverIndex) return;

      // Перемещаем элемент из dragIndex в hoverIndex
      const draggedItem = sectionItems[dragIndex];

      // Удаляем элемент из исходной позиции
      sectionItems.splice(dragIndex, 1);

      // Корректируем индекс вставки, если перетаскиваем вниз
      const insertIndex = dragIndex < hoverIndex ? hoverIndex - 1 : hoverIndex;

      // Вставляем элемент на новую позицию
      sectionItems.splice(insertIndex, 0, draggedItem);

      state.sideBarMenuAllOptionsState[section] = sectionItems;
    },
  },
});

export const {
  toggleItem,
  toggleSidebar,
  toggleOpenCustomization,
  toggleCloseCustomization,
  chooseAll,
  clearAll,
  setSidebarConfig,
  reorderSidebarItems,
} = sideBarMenuSlice.actions;
export default sideBarMenuSlice.reducer;
